import type { RequestTourValues, TourAvailableDates } from './types'
import type { FormikProps } from 'formik'

import { Box } from '@mui/material'
import TextInput from '@rentspree/component-2023.components.atoms.text-input'
import { Typography } from '@rentspree/component-2023.components.atoms.typography'
import InteractiveCard from '@rentspree/component-2023.components.organisms.interactive-card'
import dayjs from 'dayjs'
import { range } from 'lodash'
import { useState, useMemo, useEffect } from 'react'

import styles from './request-tour-form.module.scss'

interface ScheduleTourFormProps {
  formik: FormikProps<RequestTourValues>
}

export const ScheduleTourForm: React.FC<ScheduleTourFormProps> = ({ formik }) => {
  const defaultTourAvailableDates: TourAvailableDates[] = useMemo(
    () =>
      range(1, 7).map((dayNumber) => {
        const date = dayjs().add(dayNumber, 'day')
        return {
          id: `date${dayNumber}`,
          value: date.format('ddd, MMM D'),
          title: date.format('ddd'),
          subtitle: date.format('MMM D'),
          selected: false,
        }
      }),
    [],
  )
  const [tourAvailableDates, setTourAvailableDates] =
    useState<TourAvailableDates[]>(defaultTourAvailableDates)
  const { values, handleChange, handleBlur, touched, errors, setFieldValue, setFieldTouched } =
    formik

  const toggleTourDate = (dateId) => {
    const updatedTourAvailDates = tourAvailableDates.map((date) =>
      date.id === dateId ? { ...date, selected: !date.selected, disabled: false } : date,
    )
    setTourAvailableDates(updatedTourAvailDates)

    const selectedDatesValues: string[] = updatedTourAvailDates
      .filter((date) => !!date.selected)
      .map((date) => date.value)
    void setFieldValue('selectedDates', selectedDatesValues)
  }

  useEffect(() => {
    const updatedDates = defaultTourAvailableDates.map((date) => {
      if ((values.selectedDates || []).includes(date.value)) {
        return {
          ...date,
          selected: true,
        }
      }
      return date
    })
    setTourAvailableDates(updatedDates)
  }, [values.selectedDates])

  return (
    <Box data-testid="schedule-tour-form">
      <Box display="flex" flexDirection="column" mb={3}>
        <Typography variant="title" mb={0}>
          When would you like to tour?
        </Typography>
        <Typography variant="body">Select up to 3 dates</Typography>
      </Box>
      <Box data-testid="interactive-tour-date" className={styles.interactiveTourDate}>
        {tourAvailableDates.map((date) => {
          const isDisabled =
            values.selectedDates &&
            values.selectedDates.length >= 3 &&
            !tourAvailableDates.some((_date) => _date.selected && _date.id === date.id)

          return (
            <InteractiveCard
              key={date.id}
              date-testid={`interactive-date-card-${date.id}`}
              className={`${styles.availableDateCard} ${
                !date.selected ? styles.notSelectedHoverDateCard : ''
              }`}
              sx={{
                margin: date.selected ? '0px 1px' : '1px',
              }}
              isSelected={date.selected}
              disabled={isDisabled}
              isError={!!touched?.selectedDates && !!errors.selectedDates}
              onBlur={() => setFieldTouched('selectedDates', true)}
              onClick={() => !isDisabled && toggleTourDate(date.id)}
            >
              <Typography>{date.title}</Typography>
              <Typography fontWeight={700}>{date.subtitle}</Typography>
            </InteractiveCard>
          )
        })}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }} gap={1} mb={3}>
        <Typography variant="title">Message</Typography>
        <TextInput
          label="Add a message (optional)"
          name="message"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.message}
          error={!!touched?.message && !!errors?.message}
          minRows={4}
          multiline
          validTextCount={1000}
          helperText=" "
          inputProps={{ maxLength: 1000 }}
        />
      </Box>
    </Box>
  )
}
